import { invoke } from '~/lib/tauriInvoke'
import { toast } from 'vue-sonner'
import { useRouter } from 'vue-router'
import { useGroupsStore } from '~/stores/groups'
import { useUserStore } from '~/stores/user'
import { usePlatform } from '~/composables/usePlatform'
import { isTauriRuntime } from '~/lib/platform/runtime'
import {
  CHAT_MESSAGE_PUSH_TYPE,
  parsePushNavigation,
  pendingPushNavigation,
  pushNavigationKey,
  pushNavigationRoute,
  type ChatMessagePushTarget,
  type PushNavigationTarget,
} from '~/lib/push/pushNavigation'

/**
 * Navigation coordinator for push notifications — the middle of the chain:
 *
 *   FCM -> native tap handler -> pending_push.json -> `take_pending_push_navigation`
 *        -> `parsePushNavigation` -> [this] -> chat route -> `useTargetMessageNavigation`
 *        -> `useMessageJump` -> scroll + highlight
 *
 * Nothing here knows how a push is delivered, and nothing in the delivery layers knows how the app
 * navigates — the only thing crossing the boundary is a `PushNavigationTarget`.
 *
 * Covers all three app states with one mechanism:
 *   - cold start   — the file is already on disk when the webview boots; `processStartupPush()`
 *                    (called once the session is ready) picks it up.
 *   - background   — same file, written at tap time; the Rust watcher's `push://pending` event and
 *                    the visibility handler both lead back here.
 *   - foreground   — the Rust watcher notices the file within ~1s and emits `push://pending`.
 */

const PENDING_PUSH_EVENT = 'push://pending'

// Module-level: the listeners below are registered once for the app's lifetime, not per component.
let listenersAttached = false
let processing = false

export function usePushNavigation() {
  const router = useRouter()
  const groupsStore = useGroupsStore()
  const userStore = useUserStore()
  const { isMobile } = usePlatform()

  /** Where "this chat is gone" lands the user: the chat list they came from. */
  function chatListRoute() {
    return isMobile.value ? '/groups' : '/dashboard'
  }

  /** Reads (and clears) whatever the native side left behind, and queues it if it's actionable. */
  async function pollPendingPush(reason: string): Promise<boolean> {
    let raw: unknown
    try {
      raw = await invoke('take_pending_push_navigation')
    } catch (e: any) {
      // Desktop/browser builds don't have the command at all — not an error worth surfacing.
      console.debug(`[PushNavigation] no pending-push transport (${reason}):`, e?.message ?? e)
      return false
    }
    if (!raw) return false

    const target = parsePushNavigation(raw)
    if (!target) {
      console.warn(`[PushNavigation] push payload parsed to nothing actionable (${reason})`)
      return false
    }

    console.info(`[PushNavigation] push tapped (${reason}): payload parsed, ${pushNavigationKey(target)}`)
    if (!pendingPushNavigation.set(target)) {
      console.debug(`[PushNavigation] ${pushNavigationKey(target)} already handled, ignoring`)
      return false
    }
    return true
  }

  /**
   * Confirms the user still has this chat before navigating. The real authorization is the
   * backend's (`GetMessagesAround` checks `ReadMessageHistory`, group details 403 for a
   * non-member) — this is the client-side half of the same answer: a push naming a chat that isn't
   * in the user's own group list never opens a chat screen at all.
   */
  async function canOpen(target: ChatMessagePushTarget): Promise<boolean> {
    if (groupsStore.groups.length === 0) {
      try {
        await groupsStore.fetchGroups()
      } catch (e) {
        console.warn('[PushNavigation] could not load groups to verify access', e)
        return false
      }
    }
    return groupsStore.groups.some((group) => group.id === target.groupId)
  }

  /**
   * Navigates to whatever is pending, exactly once. Safe to call at any time: it no-ops when
   * nothing is pending, and holds the target (rather than dropping it) while the user isn't
   * authenticated yet — the cold-start case, where the tap happens long before the session exists.
   */
  async function processPendingPush() {
    if (processing) return
    const target = pendingPushNavigation.peek()
    if (!target) return

    if (!userStore.user) {
      console.info('[PushNavigation] navigation requested but no session yet, keeping it pending')
      return
    }

    processing = true
    try {
      // Taken only now that it can actually be acted on.
      pendingPushNavigation.take()
      pendingPushNavigation.markHandled(target)

      if (target.type === CHAT_MESSAGE_PUSH_TYPE && !(await canOpen(target))) {
        console.warn(`[PushNavigation] group ${target.groupId} is not available to this user, opening chat list`)
        toast.info('Сообщение больше недоступно')
        await router.push(chatListRoute())
        return
      }

      const route = pushNavigationRoute(target, { mobile: isMobile.value })
      console.info(`[PushNavigation] navigation requested -> ${route.path}`, route.query)
      // A plain `push` onto the existing chat route: the same screen the app already uses, reused
      // (never a second instance of it), with the target channel/message carried in the query —
      // the mobile group page opens that channel's chat Drawer off `?channel=`, and
      // `useTargetMessageNavigation` acts on `?message=` once the channel's history is on screen.
      await router.push(route)
      console.info('[PushNavigation] chat opened, target message handed to the chat screen')
    } catch (e) {
      // A failed navigation must never take the app down — worst case the user stays where they are.
      console.error('[PushNavigation] navigation failed', e)
    } finally {
      processing = false
    }
  }

  /** Cold start: called once the session is ready, so a tap-launched app lands on the right message. */
  async function processStartupPush() {
    if (await pollPendingPush('startup')) await processPendingPush()
    // A target queued before the session existed (tap -> login -> here) still needs acting on.
    await processPendingPush()
  }

  /**
   * Wires up the two ways a push can arrive while the app is already running. Idempotent — call it
   * from app startup and forget about it.
   */
  async function startPushNavigationListener() {
    if (listenersAttached || !isTauriRuntime()) return
    listenersAttached = true

    // Foreground/background taps: the Rust watcher (`spawn_pending_push_watcher`) pings us the
    // moment a payload lands on disk.
    try {
      const { listen } = await import('@tauri-apps/api/event')
      await listen(PENDING_PUSH_EVENT, async () => {
        console.info('[PushNavigation] push received while running')
        if (await pollPendingPush('event')) await processPendingPush()
      })
    } catch (e) {
      console.warn('[PushNavigation] could not subscribe to push events', e)
    }

    // Belt to that braces: an app resumed from background may have had its timers suspended, so
    // check on the way back in too.
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', async () => {
        if (document.visibilityState !== 'visible') return
        if (await pollPendingPush('resume')) await processPendingPush()
      })
    }
  }

  return {
    startPushNavigationListener,
    processStartupPush,
    processPendingPush,
    pollPendingPush,
  }
}
